import { Link, useNavigate } from "react-router-dom";
import { User, Heart, MessageSquare, LogOut, Plus, ChevronDown, Menu, Globe, ShieldAlert, ShieldCheck, Star, Coins, Wrench, Handshake, BarChart3, KeyRound, Compass, Palette, ArrowLeftRight, Clock, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { getTheme, setTheme as applyThemePref, applyServerTheme } from "../lib/theme";
import Logo from "./Logo";
import useAdminPendingCount from "../lib/useAdminPendingCount";
import useDealerQuickCounts from "../lib/useDealerQuickCounts";
import { DEALER_QUICK_TILES, buildDealerMobileLinks, dealerSlug, visibleDealerSections } from "../lib/dealerMenuConfig";
import useSavedSearchUnread from "../lib/useSavedSearchUnread";
import SavedSearchPillPopover from "./SavedSearchPillPopover";
import { MotorcycleIcon } from "./icons/VehicleIcons";
import CallerIdListener from "./caller/CallerIdListener";
import { LeadAlertsWatcher } from "./LeadAlertsWatcher";
import BranchContextBadge from "./BranchContextBadge";
import HeaderTimeClock from "./HeaderTimeClock";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import EmployeeSiteSwitcher from "./EmployeeSiteSwitcher";
import HomeAddressPrompt from "./HomeAddressPrompt";
import WorkspaceMenuSection from "./WorkspaceMenuSection";
import { Button } from "./ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
    DropdownMenuLabel,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuSubContent,
} from "./ui/dropdown-menu";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

export default function Header() {
    const { user, logout, setSession } = useAuth();
    // Jul-2026 (User-Wunsch): Der Header-Dropdown zeigt IMMER die eingeloggte
    // Person mit ihrer eigenen Login-E-Mail — unabhängig von einem hinterlegten
    // Ansprechpartner (der gehört ins Geschäfts-/Öffentlichkeits-Profil, nicht
    // in die eigene Identität).
    const headerName = user?.name;
    const headerEmail = user?.email;
    const _ownPhoto = user?.profile_photo_url || "";
    const _initials = (headerName || "")
        .split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
    const ContactAvatar = ({ className = "w-6 h-6" }) => {
        if (_ownPhoto) {
            return <img src={_ownPhoto} alt={headerName || ""} className={`${className} rounded-full object-cover border border-black/10`} data-testid="header-user-avatar" />;
        }
        if (_initials) {
            return (
                <span className={`${className} rounded-full bg-[#111827] text-[#FFCC00] inline-flex items-center justify-center text-[10px] font-black shrink-0`} data-testid="header-user-initials">
                    {_initials}
                </span>
            );
        }
        return <User className="w-4 h-4" />;
    };
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    // Jul-2026 (User-Wunsch) — Beim Abmelden prüfen, ob noch eingestempelt:
    // dann fragen „Soll ich dich im Arbeitszeitkonto abmelden? Ja/Nein".
    const [logoutPunchOpen, setLogoutPunchOpen] = useState(false);
    const [logoutBusy, setLogoutBusy] = useState(false);
    const handleLogout = async () => {
        setMobileOpen(false);
        try {
            const { data } = await api.get("/me/time/punch-state");
            if (data?.linked && data?.clocked_in) { setLogoutPunchOpen(true); return; }
        } catch { /* kein verknüpftes Arbeitszeitkonto */ }
        logout();
    };
    const confirmLogout = async (punchOut) => {
        setLogoutBusy(true);
        try {
            if (punchOut) {
                await api.post("/me/time/reminder-punch", {});
                toast.success("Ausgestempelt — Feierabend!");
            }
        } catch {
            toast.error("Ausstempeln fehlgeschlagen — bitte im Arbeitszeitkonto prüfen.");
        } finally {
            setLogoutBusy(false);
            setLogoutPunchOpen(false);
            logout();
        }
    };
    // Jun-2026 (User-Wunsch) — umschaltbares System-Design (Standard-Gelb ↔ Orange/Dunkel).
    const [theme, setThemeState] = useState(getTheme());
    const toggleTheme = () => {
        const next = theme === "tw-orange" ? "default" : "tw-orange";
        applyThemePref(next, { persist: true });
        setThemeState(next);
    };
    // Server-Präferenz (ui_theme aus /auth/me) geräteübergreifend anwenden.
    useEffect(() => {
        const pref = user?.ui_theme;
        if (pref && pref !== theme) {
            applyServerTheme(pref);
            setThemeState(pref);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.ui_theme]);
    const { t, i18n } = useTranslation();
    const lang = i18n.resolvedLanguage || i18n.language || "de";
    // Jul-2026 (User-Wunsch) — Kontowechsler für verknüpfte Konten (Mitarbeiter ↔ privat).
    const [linkedAccounts, setLinkedAccounts] = useState([]);
    const [autoChoiceOn, setAutoChoiceOn] = useState(() => {
        try { return localStorage.getItem("tw_account_choice_auto") === "1"; } catch { return false; }
    });
    useEffect(() => {
        if (!user?.id) { setLinkedAccounts([]); return; }
        api.get("/auth/linked-accounts")
            .then((r) => setLinkedAccounts(r.data?.items || []))
            .catch(() => setLinkedAccounts([]));
    }, [user?.id]);
    const switchAccount = async (acc) => {
        try {
            const { data } = await api.post("/auth/switch-account", { target_user_id: acc.id });
            setSession(data.user, data.token);
            try { localStorage.setItem("tw_last_account_id", acc.id); } catch { /* ignore */ }
            toast.success(`Gewechselt zu ${data.user?.name || acc.name}`);
            const r = data.user?.role;
            // Voller Reload: setzt alle nutzerbezogenen Caches/Guards sauber zurück.
            const path = r === "vermittler" ? "/vermittler"
                : r === "service_provider" ? "/dienstleister/dashboard"
                : r === "admin" ? "/admin/dealers" : "/dashboard";
            window.location.assign(path);
        } catch {
            toast.error("Kontowechsel fehlgeschlagen.");
        }
    };
    const toggleLang = () => i18n.changeLanguage(lang === "de" ? "en" : "de");
    const adminPending = useAdminPendingCount();
    const savedSearchUnread = useSavedSearchUnread();
    const isAdmin = user && user !== false && user.role === "admin";
    const isB2B = user && user !== false && user.role === "b2b";
    const quickCounts = useDealerQuickCounts(isB2B);
    const showPendingPill = isAdmin && typeof adminPending === "number" && adminPending > 0;
    const showSavedSearchPill = (
        user && user !== false && !isAdmin &&
        typeof savedSearchUnread === "number" && savedSearchUnread > 0
    );

    // Jun-2026 (User-Wunsch) — Mobile-Menü: rollenspezifische Menüpunkte sind auf
    // dem Handy jetzt erreichbar (vorher fehlten sie komplett). Baut dieselben
    // Ziele wie das Desktop-Dropdown als flache Liste.
    const buildRoleLinks = () => {
        if (!user || user === false) return [];
        if (user.role === "admin") return [
            { label: "Admin-Übersicht", path: "/admin" },
            { label: "Einstellungen", path: "/admin/konten" },
            { label: "Betreiberdaten (Impressum)", path: "/admin/betreiber" },
            { label: "E-Mail-Center (Postfächer)", path: "/dealer/email" },
            { label: "Startseite (Käufersicht)", path: "/" },
            { label: "Händler", path: "/admin/dealers" },
            { label: "Konto-Wechsel", path: "/admin/impersonation" },
            { label: "Compliance", path: "/admin/compliance" },
            { label: "Vermittler", path: "/admin/vermittlers" },
            { label: "Empfehlungs-Leads", path: "/admin/vermittler-referrals" },
            { label: "SMS-Verlauf", path: "/admin/sms/history" },
            { label: "Portal-Zustimmungen", path: "/admin/portal-consent" },
            { label: "DATEV-Auto-Versand", path: "/admin/datev/auto-send" },
            { label: "Cron-Status", path: "/admin/cron-status" },
            { label: "Health-Monitor", path: "/admin/health-monitor" },
            { label: "KBA-Datenbank (gelernt)", path: "/admin/hsn-tsn" },
            { label: "Marktbeobachtung", path: "/admin/market-watch" },
            { label: "Systempflege · Fahrzeug-Doubletten", path: "/admin/vehicle-duplicates" },
            { label: "Systempflege · Kunden-Doubletten", path: "/admin/customer-duplicates" },
            { label: "Bewertungs-Moderation", path: "/admin/reviews" },
            { label: "€Punkte-Verwaltung", path: "/admin/points" },
            { label: "Pfand & Refinanzierung", path: "/admin/pawn" },
            { label: "Dienstleister", path: "/admin/service-providers" },
            { label: "Wallet-Verwaltung", path: "/admin/wallet" },
        ];
        if (user.role === "vermittler") return [
            { label: "Vermittler & Verkäufer-Dashboard", path: "/vermittler" },
            { label: "Bilddatenbank", path: "/dealer/bilddatenbank" },
            { label: "Favoriten", path: "/dashboard?tab=favorites" },
            { label: "Nachrichten", path: "/dashboard?tab=messages" },
        ];
        if (user.role === "service_provider") return [
            { label: "Dienstleister-Dashboard", path: "/dienstleister/dashboard" },
            { label: "Nachrichten", path: "/dashboard?tab=messages" },
        ];
        const links = [{ label: "Mein Konto", path: "/dashboard" }];
        if (user.role === "b2b" || user.role === "zweiradverkaeufer") {
            // Jul-2026 (Chef-Wunsch) — Mitarbeiter sehen das volle Arbeitsmenü
            // (rollen-/modulgefiltert in visibleDealerSections).
            links.push(...buildDealerMobileLinks(user));
        }
        return links;
    };
    const roleLinks = buildRoleLinks();
    // Jul-2026 (User-Wunsch): Mobile-Menü startet IMMER eingeklappt — Unterpunkte
    // (indent) werden je Sektion erst nach Tipp auf den Pfeil angezeigt.
    const groupedRoleLinks = [];
    for (const link of roleLinks) {
        if (link.indent && groupedRoleLinks.length) groupedRoleLinks[groupedRoleLinks.length - 1].children.push(link);
        else groupedRoleLinks.push({ ...link, children: [] });
    }
    const [expandedSections, setExpandedSections] = useState({});
    // Merkt sich die zuletzt aufgeklappte Sektion pro Nutzer (localStorage).
    const memKey = user && user !== false ? `tw_mobile_open_sec_${user.id}` : null;
    useEffect(() => {
        if (!mobileOpen) return;
        let init = {};
        try {
            const remembered = memKey && localStorage.getItem(memKey);
            if (remembered) init = { [remembered]: true };
        } catch { /* noop */ }
        setExpandedSections(init);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mobileOpen]);
    const toggleSection = (label) => setExpandedSections((s) => {
        const next = { ...s, [label]: !s[label] };
        try {
            if (memKey) {
                if (next[label]) localStorage.setItem(memKey, label);
                else if (localStorage.getItem(memKey) === label) localStorage.removeItem(memKey);
            }
        } catch { /* noop */ }
        return next;
    });
    const goMobile = (link) => {
        setMobileOpen(false);
        if (link.external) window.open(link.external, "_blank", "noopener,noreferrer");
        else navigate(link.path);
    };
    // Jun-2026 (User-Wunsch) — Schnellzugriff-Kacheln ganz oben im Mobile-Menü
    // für die wichtigsten Händler-Aktionen (spart Scrollen auf dem Handy).
    // Quelle: zentrale dealerMenuConfig (Mobile = Kacheln ohne `dashOnly`).
    const quickTiles = (user && user !== false && user.role === "b2b")
        ? DEALER_QUICK_TILES.filter((t) => !t.dashOnly).map((t) => ({
            label: t.label,
            icon: t.icon,
            path: t.path,
            testid: `mobile-quick-${t.key}`,
            badge: t.badgeKey ? quickCounts[t.badgeKey] : 0,
        }))
        : [];

    return (
        <header className="bg-[#FFCC00] text-[#111827] sticky top-0 z-50 border-b border-black/10" data-testid="site-header">
            {isB2B && <CallerIdListener userId={user.id} />}
            {/* Jul-2026 (User-Wunsch) — sofortige Lead-Benachrichtigung für Verkäufer */}
            {user && <LeadAlertsWatcher role={user.role} />}
            <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
                <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
                    <Logo className="contents" />
                </Link>

                <nav className="hidden lg:flex items-center gap-6 font-semibold text-sm">
                    <Link to="/discover" className="hover:underline underline-offset-4 decoration-2 inline-flex items-center gap-1 text-indigo-800 font-bold" data-testid="nav-discover">
                        <Compass className="w-4 h-4" /> Entdecken
                    </Link>
                    <Link to="/search?category=motorcycle" className="hover:underline underline-offset-4 decoration-2" data-testid="nav-vehicle-search">{t("nav.vehicle_search")}</Link>
                    <Link to="/auctions" className="hover:underline underline-offset-4 decoration-2 inline-flex items-center gap-1" data-testid="nav-auctions">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" />
                        {t("nav.auctions")}
                    </Link>
                    <Link to="/search?category=motorcycle&sale_type=rental" className="hover:underline underline-offset-4 decoration-2" data-testid="nav-rent">{t("nav.rent")}</Link>
                    <Link to="/search?category=motorcycle&sale_type=pawn" className="hover:underline underline-offset-4 decoration-2 inline-flex items-center gap-1" data-testid="nav-pawn">
                        <Coins className="w-4 h-4" /> {t("nav.pawn")}
                    </Link>
                    <Link to="/ankauf" className="hover:underline underline-offset-4 decoration-2 text-[#991B1B] font-bold" data-testid="nav-ankauf">{t("nav.sell")}</Link>
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    <EmployeeSiteSwitcher />
                    <HeaderTimeClock />
                    <HomeAddressPrompt />
                    <BranchContextBadge user={user} />
                    <Link
                        to="/was-kann-twowheels#ki"
                        className="bg-[#111827] hover:bg-black text-[#FFCC00] px-3 h-10 rounded-sm inline-flex items-center gap-1.5 font-bold text-xs uppercase tracking-wider transition-colors"
                        data-testid="nav-ki-features"
                    >
                        ✨ KI
                    </Link>
                    <button
                        onClick={toggleLang}
                        className="flex items-center gap-1 px-2 h-9 rounded-sm hover:bg-black/10 text-sm font-bold uppercase tracking-wider"
                        data-testid="lang-toggle"
                        title={lang === "de" ? t("nav.switch_to_en") : t("nav.switch_to_de")}
                    >
                        <Globe className="w-4 h-4" /> {lang.toUpperCase()}
                    </button>
                    {user && user !== false ? (
                        <>
                            {showPendingPill && (
                                <button
                                    type="button"
                                    onClick={() => navigate("/admin")}
                                    className="group relative inline-flex items-center gap-1.5 h-10 px-3 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black font-bold text-xs uppercase tracking-wider transition-colors"
                                    data-testid="admin-pending-pill"
                                    title={t("admin_pending_pill.tooltip", { count: adminPending })}
                                    aria-label={t("admin_pending_pill.aria", { count: adminPending })}
                                >
                                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 animate-ping" aria-hidden="true" />
                                    <ShieldAlert className="w-4 h-4" />
                                    <span className="font-mono-num" data-testid="admin-pending-count">{adminPending}</span>
                                    <span className="hidden xl:inline">{t("admin_pending_pill.label")}</span>
                                </button>
                            )}
                            {showSavedSearchPill && (
                                <SavedSearchPillPopover count={savedSearchUnread} />
                            )}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="flex items-center gap-2 bg-white/40 hover:bg-white/60 transition-colors rounded-sm px-3 h-10 font-semibold text-sm" data-testid="user-menu-trigger">
                                        <ContactAvatar className="w-6 h-6" />
                                        <span className="max-w-[120px] truncate" data-testid="header-profile-name">{headerName}</span>
                                        <ChevronDown className="w-3 h-3" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56" onCloseAutoFocus={(e) => e.preventDefault()}>
                                    <DropdownMenuLabel>
                                        <div className="flex items-center gap-2.5">
                                            <ContactAvatar className="w-9 h-9" />
                                            <div className="min-w-0">
                                                <div className="font-bold truncate" data-testid="header-profile-name-full">{headerName}</div>
                                                <div className="text-xs text-gray-500 font-normal truncate">
                                                    {user.role === "admin" ? t("nav.role_admin") : user.role === "b2b" ? t("nav.role_b2b") : user.role === "vermittler" ? "Vermittler" : user.role === "zweiradverkaeufer" ? `Mitarbeiter${user.employee_function ? " · " + user.employee_function : ""}` : t("nav.role_b2c")} · {headerEmail}
                                                </div>
                                            </div>
                                        </div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {user.role === "admin" ? (
                                        <>
                                            <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin-dashboard">
                                                <BarChart3 className="w-4 h-4 mr-2" /> Admin-Übersicht
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate("/")} data-testid="menu-admin-home">
                                                <User className="w-4 h-4 mr-2" /> {t("nav.menu_admin_home")}
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate("/admin/dealers")} data-testid="menu-admin-dealers">
                                                <ShieldAlert className="w-4 h-4 mr-2" />
                                                <span className="flex-1">{t("nav.menu_admin_dealers")}</span>
                                                {showPendingPill && (
                                                    <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-sm bg-[#FFCC00] text-[#111827] text-xs font-black font-mono-num" data-testid="menu-admin-pending-badge">
                                                        {adminPending}
                                                    </span>
                                                )}
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate("/admin/compliance")} data-testid="menu-admin-compliance">
                                                <ShieldCheck className="w-4 h-4 mr-2" /> {t("nav.menu_admin_compliance")}
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate("/admin/vermittlers")} data-testid="menu-admin-vermittlers">
                                                <ShieldAlert className="w-4 h-4 mr-2" /> Vermittler
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate("/admin/vermittler-referrals")} data-testid="menu-admin-vermittler-referrals">
                                                <Handshake className="w-4 h-4 mr-2" /> Empfehlungs-Leads
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate("/admin/sms/history")} data-testid="menu-admin-sms">
                                                <MessageSquare className="w-4 h-4 mr-2" /> SMS-Verlauf
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate("/admin/datev/auto-send")} data-testid="menu-admin-datev">
                                                <ShieldCheck className="w-4 h-4 mr-2" /> DATEV-Auto-Versand
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate("/admin/cron-status")} data-testid="menu-admin-cron">
                                                <ShieldCheck className="w-4 h-4 mr-2" /> Cron-Status
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate("/admin/health-monitor")} data-testid="menu-admin-health">
                                                <ShieldCheck className="w-4 h-4 mr-2" /> Health-Monitor
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate("/admin/hsn-tsn")} data-testid="menu-admin-hsn-tsn">
                                                <ShieldCheck className="w-4 h-4 mr-2" /> KBA-Datenbank (gelernt)
                                            </DropdownMenuItem>
                                            {/* Jun-2026 (User-Wunsch): Doubletten als Unterbereiche unter „Systempflege" gebündelt */}
                                            <DropdownMenuSub>
                                                <DropdownMenuSubTrigger data-testid="menu-admin-systempflege">
                                                    <Wrench className="w-4 h-4 mr-2" /> Systempflege
                                                </DropdownMenuSubTrigger>
                                                <DropdownMenuSubContent>
                                                    <DropdownMenuItem onClick={() => navigate("/admin/vehicle-duplicates")} data-testid="menu-admin-vehicle-dupes">
                                                        <ShieldCheck className="w-4 h-4 mr-2" /> Fahrzeug-Doubletten
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => navigate("/admin/customer-duplicates")} data-testid="menu-admin-customer-dupes">
                                                        <ShieldCheck className="w-4 h-4 mr-2" /> Kunden-Doubletten
                                                    </DropdownMenuItem>
                                                </DropdownMenuSubContent>
                                            </DropdownMenuSub>
                                            <DropdownMenuItem onClick={() => navigate("/admin/reviews")} data-testid="menu-admin-reviews">
                                                <Star className="w-4 h-4 mr-2" /> Bewertungs-Moderation
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate("/admin/points")} data-testid="menu-admin-points">
                                                <Coins className="w-4 h-4 mr-2" /> €Punkte-Verwaltung
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate("/admin/pawn")} data-testid="menu-admin-pawn">
                                                <Coins className="w-4 h-4 mr-2" /> Pfand &amp; Refinanzierung
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate("/admin/service-providers")} data-testid="menu-admin-service-providers">
                                                <Handshake className="w-4 h-4 mr-2" /> Dienstleister
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate("/admin/wallet")} data-testid="menu-admin-wallet">
                                                <Coins className="w-4 h-4 mr-2" /> Wallet-Verwaltung
                                            </DropdownMenuItem>
                                        </>
                                    ) : user.role === "vermittler" ? (
                                        <>
                                            <WorkspaceMenuSection user={user} />
                                            {/* Aug-2026 (User-Wunsch): Bilddatenbank auch für Vermittler */}
                                            <DropdownMenuItem onClick={() => navigate("/dealer/bilddatenbank")} data-testid="menu-vermittler-bilddatenbank">
                                                <Camera className="w-4 h-4 mr-2" /> Bilddatenbank
                                            </DropdownMenuItem>
                                        </>
                                    ) : user.role === "service_provider" ? (
                                        <>
                                            <DropdownMenuItem onClick={() => navigate("/dienstleister/dashboard")} data-testid="menu-service-provider">
                                                <Wrench className="w-4 h-4 mr-2" /> Dienstleister-Dashboard
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate("/dashboard?tab=messages")} data-testid="menu-messages-sp">
                                                <MessageSquare className="w-4 h-4 mr-2" /> {t("nav.menu_messages")}
                                            </DropdownMenuItem>
                                        </>
                                    ) : (
                                        <>
                                            {user.role !== "b2b" && (
                                                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                                                    <User className="w-4 h-4 mr-2" /> {t("nav.menu_account")}
                                                </DropdownMenuItem>
                                            )}
                                            {/* Phase 3.48 — Mein Inserate, Favoriten, Nachrichten entfernt (jetzt direkt im Dashboard als Gruppen-Tabs sichtbar) */}
                                            {(user.role === "b2b" || user.role === "zweiradverkaeufer") && (
                                                <>
                                                    {/* Feb-2026 (User-Wunsch 27.05.2026) — "Statistik" wurde aus dem Header-Dropdown entfernt
                                                        und ist jetzt fest im Händler-Dashboard unter dem KI-Aktivitäts-Feed eingebettet. */}
                                                    {/* Jul-2026 (Chef-Wunsch) — Mitarbeiter sehen das volle Arbeitsmenü (rollen-/modulgefiltert). */}
                                                    {visibleDealerSections(user).map((sec) => {
                                                        if (sec.kind === "item") {
                                                            const Icon = sec.icon;
                                                            const secBadge = sec.badgeKey ? (quickCounts[sec.badgeKey] || 0) : 0;
                                                            return (
                                                                <DropdownMenuItem key={sec.testid} onClick={() => navigate(sec.path)} data-testid={sec.testid}>
                                                                    <Icon className="w-4 h-4 mr-2" /> {sec.label}
                                                                    {secBadge > 0 && (
                                                                        <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-black font-mono-num leading-none" data-testid={`${sec.testid}-badge`}>
                                                                            {secBadge > 99 ? "99+" : secBadge}
                                                                        </span>
                                                                    )}
                                                                </DropdownMenuItem>
                                                            );
                                                        }
                                                        const SubIcon = sec.icon;
                                                        return (
                                                            <DropdownMenuSub key={sec.triggerTestid}>
                                                                <DropdownMenuSubTrigger data-testid={sec.triggerTestid}>
                                                                    <SubIcon className="w-4 h-4 mr-2" /> {sec.label}
                                                                </DropdownMenuSubTrigger>
                                                                <DropdownMenuSubContent className={sec.subWidth || "w-56"}>
                                                                    {sec.items.map((it) => {
                                                                        const ItemIcon = it.icon;
                                                                        const itemBadge = it.badgeKey ? (quickCounts[it.badgeKey] || 0) : 0;
                                                                        const onClick = it.onlineshop
                                                                            ? () => window.open(`/shop/${dealerSlug(user)}`, "_blank", "noopener,noreferrer")
                                                                            : () => navigate(it.path);
                                                                        return (
                                                                            <DropdownMenuItem key={it.testid} onClick={onClick} data-testid={it.testid}>
                                                                                <ItemIcon className="w-4 h-4 mr-2" /> {it.label}
                                                                                {itemBadge > 0 && (
                                                                                    <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-black font-mono-num leading-none" data-testid={`${it.testid}-badge`}>
                                                                                        {itemBadge > 99 ? "99+" : itemBadge}
                                                                                    </span>
                                                                                )}
                                                                            </DropdownMenuItem>
                                                                        );
                                                                    })}
                                                                </DropdownMenuSubContent>
                                                            </DropdownMenuSub>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </>
                                    )}
                                    <DropdownMenuSeparator />
                                    {linkedAccounts.length > 0 && (
                                        <>
                                            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-gray-500">Konto wechseln</DropdownMenuLabel>
                                            {linkedAccounts.map((acc) => (
                                                <DropdownMenuItem key={acc.id} onClick={() => switchAccount(acc)} data-testid={`menu-switch-account-${acc.id}`}>
                                                    <ArrowLeftRight className="w-4 h-4 mr-2 shrink-0" />
                                                    <span className="min-w-0">
                                                        <span className="block truncate font-medium">{acc.name || acc.email}</span>
                                                        <span className="block truncate text-[10px] text-gray-500">
                                                            {acc.role === "zweiradverkaeufer"
                                                                ? `Mitarbeiter${acc.employer_name ? " · " + acc.employer_name : ""}`
                                                                : acc.role === "vermittler" ? "Vermittler (privat)"
                                                                : acc.role === "b2b" ? "Händler" : "Privat"}
                                                        </span>
                                                    </span>
                                                </DropdownMenuItem>
                                            ))}
                                            {autoChoiceOn && (
                                                <DropdownMenuItem
                                                    onClick={() => {
                                                        try { localStorage.setItem("tw_account_choice_auto", ""); } catch { /* ignore */ }
                                                        setAutoChoiceOn(false);
                                                        toast.success("Beim nächsten Login wird wieder gefragt.");
                                                    }}
                                                    data-testid="menu-reset-account-choice"
                                                >
                                                    <ArrowLeftRight className="w-4 h-4 mr-2 shrink-0" />
                                                    <span className="min-w-0">
                                                        <span className="block font-medium">Beim Login wieder fragen</span>
                                                        <span className="block text-[10px] text-gray-500">Automatische Konto-Wahl zurücksetzen</span>
                                                    </span>
                                                </DropdownMenuItem>
                                            )}
                                            <DropdownMenuSeparator />
                                        </>
                                    )}
                                    <DropdownMenuItem onClick={(e) => { e.preventDefault(); toggleTheme(); }} data-testid="menu-theme-toggle">
                                        <Palette className="w-4 h-4 mr-2" /> {theme === "tw-orange" ? "Standard-Design (Gelb)" : "Orange-Design (Dunkel)"}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate("/passkeys")} data-testid="menu-passkeys">
                                        <KeyRound className="w-4 h-4 mr-2" /> Passkeys · Gerätelogin
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                                        <LogOut className="w-4 h-4 mr-2" /> {t("nav.logout")}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="font-semibold text-sm hover:underline underline-offset-4 decoration-2" data-testid="nav-login">
                                {t("nav.login")}
                            </Link>
                            <Button
                                onClick={() => navigate("/register")}
                                className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm px-5 h-10"
                                data-testid="nav-register"
                            >
                                {t("nav.register")}
                            </Button>
                        </>
                    )}
                </div>

                <button
                    className="md:hidden bg-[#111827] text-[#FFCC00] rounded-sm w-10 h-10 flex items-center justify-center"
                    onClick={() => setMobileOpen(!mobileOpen)}
                    data-testid="mobile-menu-toggle"
                    aria-label={mobileOpen ? "Menü schließen" : "Menü öffnen"}
                    aria-expanded={mobileOpen}
                >
                    <Menu className="w-5 h-5" aria-hidden="true" />
                </button>
            </div>

            {mobileOpen && (
                <div className="md:hidden bg-[#FFCC00] border-t border-black/10 px-4 py-4 space-y-3 max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="mobile-menu">
                    {quickTiles.length > 0 && (
                        <div className="grid grid-cols-2 gap-2" data-testid="mobile-quick-tiles">
                            {quickTiles.map((tile) => {
                                const Icon = tile.icon;
                                return (
                                    <button
                                        key={tile.label}
                                        onClick={() => { setMobileOpen(false); navigate(tile.path); }}
                                        className="relative flex flex-col items-center justify-center gap-1.5 bg-[#111827] text-[#FFCC00] rounded-sm py-3 px-1 font-bold text-xs uppercase tracking-wider active:scale-95 transition-transform"
                                        data-testid={tile.testid}
                                    >
                                        {tile.badge > 0 && (
                                            <span className="absolute top-1 right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-black font-mono-num leading-none" data-testid={`${tile.testid}-badge`}>
                                                {tile.badge > 99 ? "99+" : tile.badge}
                                            </span>
                                        )}
                                        <Icon className="w-5 h-5" />
                                        <span className="leading-tight text-center">{tile.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    <Link to="/discover" className="block font-semibold text-indigo-800" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-discover">Entdecken</Link>
                    <Link to="/search?category=motorcycle" className="block font-semibold" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-vehicle-search">{t("nav.vehicle_search")}</Link>
                    <Link to="/auctions" className="block font-semibold" onClick={() => setMobileOpen(false)}>Auktionen</Link>
                    <Link to="/search?category=motorcycle&sale_type=rental" className="block font-semibold" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-rent">{t("nav.rent")}</Link>
                    <Link to="/search?category=motorcycle&sale_type=pawn" className="block font-semibold" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-pawn">{t("nav.pawn")}</Link>
                    <Link to="/ankauf" className="block font-semibold text-[#991B1B]" onClick={() => setMobileOpen(false)}>Verkaufen</Link>
                    <Link to="/was-kann-twowheels#ki" className="block font-bold text-sm text-[#111827]" onClick={() => setMobileOpen(false)}>✨ KI-Funktionen</Link>
                    <button onClick={() => { toggleLang(); }} className="flex items-center gap-1.5 font-bold text-sm uppercase tracking-wider" data-testid="mobile-lang-toggle">
                        <Globe className="w-4 h-4" /> {lang.toUpperCase()}
                    </button>
                    <button onClick={toggleTheme} className="flex items-center gap-1.5 font-bold text-sm" data-testid="mobile-theme-toggle">
                        <Palette className="w-4 h-4" /> {theme === "tw-orange" ? "Standard-Design" : "Orange-Design"}
                    </button>

                    {user && user !== false ? (
                        <>
                            {/* Inserat erstellen */}
                            <div className="border-t border-black/20 pt-3">
                                <button
                                    onClick={() => { setMobileOpen(false); window.location.assign("/create-listing"); }}
                                    className="w-full inline-flex items-center justify-center gap-2 bg-[#111827] text-white font-bold rounded-sm h-11"
                                    data-testid="mobile-create-listing-btn"
                                >
                                    <Plus className="w-4 h-4" /> {t("nav.create_listing")}
                                </button>
                            </div>

                            {/* Rollenspezifische Menüpunkte — Sektionen eingeklappt, Pfeil klappt auf */}
                            {groupedRoleLinks.length > 0 && (
                                <div className="border-t border-black/20 pt-3 space-y-1" data-testid="mobile-role-links">
                                    {groupedRoleLinks.map((link) => {
                                        const slug = link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
                                        const open = !!expandedSections[link.label];
                                        return (
                                            <div key={link.label}>
                                                <div className="flex items-center">
                                                    <button
                                                        onClick={() => goMobile(link)}
                                                        className="flex-1 text-left font-semibold text-[15px] py-1.5"
                                                        data-testid={`mobile-link-${slug}`}
                                                    >
                                                        {link.label}
                                                    </button>
                                                    {link.children.length > 0 && (
                                                        <button
                                                            onClick={() => toggleSection(link.label)}
                                                            className="p-2 -mr-2"
                                                            aria-label={`${link.label} ${open ? "einklappen" : "aufklappen"}`}
                                                            data-testid={`mobile-section-toggle-${slug}`}
                                                        >
                                                            <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
                                                        </button>
                                                    )}
                                                </div>
                                                {open && link.children.map((child) => (
                                                    <button
                                                        key={child.label}
                                                        onClick={() => goMobile(child)}
                                                        className="block w-full text-left font-semibold text-[15px] py-1.5 pl-4 text-[#111827]/80"
                                                        data-testid={`mobile-link-${child.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
                                                    >
                                                        ↳ {child.label}
                                                    </button>
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Konto-Aktionen */}
                            <div className="border-t border-black/20 pt-3 flex gap-2">
                                <Button onClick={() => { setMobileOpen(false); navigate("/passkeys"); }} variant="outline" className="flex-1 border-[#111827]" data-testid="mobile-passkeys-btn">
                                    <KeyRound className="w-4 h-4 mr-1.5" /> Passkeys
                                </Button>
                                <Button onClick={handleLogout} variant="outline" className="border-[#111827]" data-testid="mobile-logout-btn">
                                    <LogOut className="w-4 h-4 mr-1.5" /> Abmelden
                                </Button>
                            </div>
                        </>
                    ) : (
                        <div className="border-t border-black/20 pt-3 flex gap-2">
                            <Button onClick={() => { setMobileOpen(false); navigate("/login"); }} variant="outline" className="flex-1 border-[#111827]">Anmelden</Button>
                            <Button onClick={() => { setMobileOpen(false); navigate("/register"); }} className="flex-1 bg-[#111827] text-white">Registrieren</Button>
                        </div>
                    )}
                </div>
            )}

            {/* Jul-2026 (User-Wunsch) — Abmelde-Hinweis bei offener Arbeitszeit */}
            <Dialog open={logoutPunchOpen} onOpenChange={(o) => { if (!o && !logoutBusy) setLogoutPunchOpen(false); }}>
                <DialogContent className="max-w-md" data-testid="logout-punch-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Clock className="w-5 h-5 text-emerald-600" /> Du bist noch eingestempelt
                        </DialogTitle>
                        <DialogDescription>
                            Du bist im Arbeitszeitkonto noch angemeldet und musst dich noch abmelden (ausstempeln).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="bg-amber-50 border border-amber-200 rounded-sm px-3 py-2.5 text-sm font-bold text-amber-900" data-testid="logout-punch-question">
                        Soll ich dich im Arbeitszeitkonto abmelden?
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => confirmLogout(false)} disabled={logoutBusy} data-testid="logout-punch-no">
                            Nein, eingestempelt bleiben
                        </Button>
                        <Button onClick={() => confirmLogout(true)} disabled={logoutBusy}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="logout-punch-yes">
                            {logoutBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Clock className="w-4 h-4 mr-1.5" />}
                            Ja, ausstempeln &amp; abmelden
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </header>
    );
}
